import styled, { css } from 'styled-components';

import { DotLoadingIndicator } from '@xing-com/dot-loading-indicator';
import {
  mediaConfined,
  mediaWide,
  zIndexBackgroundLayer1,
  zIndexContentLayer2,
} from '@xing-com/layout-tokens';
import { Link } from '@xing-com/link';
import {
  dropShadowM,
  motionEasingLeave,
  motionTimeM,
  motionTimeS,
  scale010,
  scale020,
  scale110,
  scale130,
  scale160,
  scale180,
  scale190,
  spaceXXS,
  xdlColorA11yBorder,
  xdlColorA11yBorderOnLight,
  xdlColorButtonDecline,
  xdlColorButtonDeclineHover,
  xdlColorButtonHighlight,
  xdlColorButtonHighlightHover,
  xdlColorControlFill,
  xdlColorControlFillInvert,
  xdlColorControlFillSecondary,
  xdlColorDisabledSoft,
  xdlColorDisabledStrong,
  xdlColorFocusRing,
  xdlColorHoverQuaternary,
  xdlColorModalBorder,
  xdlColorPrimaryButtonHover,
  xdlColorSelected,
  xdlColorText,
  xdlColorTextInvert,
  xdlColorTextOnDark,
  xdlColorTextOnLight,
  xdlColorXING,
} from '@xing-com/tokens';

import type {
  FloatingButtonSizes,
  FloatingButtonVariants,
} from './floating-button.types';

type FloatingButtonTProps = {
  $size?: FloatingButtonSizes;
  $sizeConfined?: FloatingButtonSizes;
  $sizeWide?: FloatingButtonSizes;
  $variant: FloatingButtonVariants;
};

type IconContainerTProps = {
  $size?: FloatingButtonSizes;
  $sizeConfined?: FloatingButtonSizes;
  $sizeWide?: FloatingButtonSizes;
};

type LoadingTProps = {
  $size?: FloatingButtonSizes;
  $sizeConfined?: FloatingButtonSizes;
  $sizeWide?: FloatingButtonSizes;
};

// *** Sizes ***
export const sizes = {
  small: css`
    height: ${scale160};
    width: ${scale160};
  `,
  medium: css`
    height: ${scale180};
    width: ${scale180};
  `,
  large: css`
    height: ${scale190};
    width: ${scale190};
  `,
};

// *** Variants ***
export const variants = {
  primary: css`
    color: ${xdlColorTextOnLight};

    &::after {
      background-color: ${xdlColorXING};
      border-color: ${xdlColorA11yBorderOnLight};
      border-style: solid;
      border-width: ${scale010};
    }

    &:visited {
      color: ${xdlColorTextOnLight};
    }

    &:hover,
    &:focus {
      color: ${xdlColorTextOnLight};

      &::after {
        background-color: ${xdlColorPrimaryButtonHover};
        border-color: ${xdlColorA11yBorderOnLight};
        border-style: solid;
        border-width: ${scale010};
      }
    }

    &:active {
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorSelected};
        border: none;
      }
    }

    &:disabled {
      color: ${xdlColorDisabledStrong};

      &::after {
        background-color: ${xdlColorDisabledSoft};
        border: none;
      }
    }

    &[data-loading='true'] {
      color: ${xdlColorTextOnLight};

      /** The loading dots */
      & [data-xds='DotLoadingIndicator'] > div {
        background-color: ${xdlColorTextOnLight};
      }

      &::after {
        background-color: ${xdlColorXING};
        border-color: ${xdlColorA11yBorderOnLight};
        border-style: solid;
        border-width: ${scale010};
      }
    }
  `,
  strong: css`
    color: ${xdlColorTextInvert};

    &::after {
      background-color: ${xdlColorControlFillInvert};
      border: none;
    }

    &:visited {
      color: ${xdlColorTextInvert};
    }

    &:hover,
    &:focus {
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorHoverQuaternary};
        border: none;
      }
    }

    &:active {
      color: ${xdlColorText};

      &::after {
        background-color: ${xdlColorControlFill};
        border-color: ${xdlColorModalBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }

    &:disabled {
      color: ${xdlColorDisabledStrong};

      &::after {
        background-color: ${xdlColorDisabledSoft};
        border: none;
      }
    }

    &[data-loading='true'] {
      color: ${xdlColorTextInvert};

      /** The loading dots */
      & [data-xds='DotLoadingIndicator'] > div {
        background-color: ${xdlColorTextInvert};
      }

      &::after {
        background-color: ${xdlColorSelected};
        border: none;
      }
    }
  `,
  plain: css`
    color: ${xdlColorText};

    &::after {
      background-color: ${xdlColorControlFill};
      border-color: ${xdlColorModalBorder};
      border-style: solid;
      border-width: ${scale010};
    }

    &:visited {
      color: ${xdlColorText};
    }

    &:hover,
    &:focus {
      color: ${xdlColorText};

      &::after {
        background-color: ${xdlColorControlFillSecondary};
        border-color: ${xdlColorModalBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }

    &:active {
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorSelected};
        border: none;
      }
    }

    &:disabled {
      color: ${xdlColorDisabledStrong};

      &::after {
        background-color: ${xdlColorDisabledSoft};
        border: none;
      }
    }

    &[data-loading='true'] {
      color: ${xdlColorText};

      /** The loading dots */
      & [data-xds='DotLoadingIndicator'] > div {
        background-color: ${xdlColorText};
      }

      &::after {
        background-color: ${xdlColorControlFill};
        border-color: ${xdlColorModalBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }
  `,
  highlight: css`
    color: ${xdlColorTextOnDark};

    &::after {
      background-color: ${xdlColorButtonHighlight};
      border-color: ${xdlColorA11yBorder};
      border-style: solid;
      border-width: ${scale010};
    }

    &:visited {
      color: ${xdlColorTextOnDark};
    }

    &:hover,
    &:focus {
      color: ${xdlColorTextOnDark};

      &::after {
        background-color: ${xdlColorButtonHighlightHover};
        border-color: ${xdlColorA11yBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }

    &:active {
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorSelected};
        border: none;
      }
    }

    &:disabled {
      color: ${xdlColorDisabledStrong};

      &::after {
        background-color: ${xdlColorDisabledSoft};
        border: none;
      }
    }

    &[data-loading='true'] {
      color: ${xdlColorTextOnDark};

      /** The loading dots */
      & [data-xds='DotLoadingIndicator'] > div {
        background-color: ${xdlColorTextOnDark};
      }

      &::after {
        background-color: ${xdlColorButtonHighlight};
        border-color: ${xdlColorA11yBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }
  `,
  decline: css`
    color: ${xdlColorTextOnDark};

    &::after {
      background-color: ${xdlColorButtonDecline};
      border-color: ${xdlColorA11yBorder};
      border-style: solid;
      border-width: ${scale010};
    }

    &:visited {
      color: ${xdlColorTextOnDark};
    }

    &:hover,
    &:focus {
      color: ${xdlColorTextOnDark};

      &::after {
        background-color: ${xdlColorButtonDeclineHover};
        border-color: ${xdlColorA11yBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }

    &:active {
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorSelected};
        border: none;
      }
    }

    &:disabled {
      color: ${xdlColorDisabledStrong};

      &::after {
        background-color: ${xdlColorDisabledSoft};
        border: none;
      }
    }

    &[data-loading='true'] {
      color: ${xdlColorTextOnDark};

      /** The loading dots */
      & [data-xds='DotLoadingIndicator'] > div {
        background-color: ${xdlColorTextOnDark};
      }

      &::after {
        background-color: ${xdlColorButtonDecline};
        border-color: ${xdlColorA11yBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }
  `,
};

const iconSizes = {
  small: css`
    height: ${scale110};
    width: ${scale110};

    & > svg {
      height: ${scale110};
      width: ${scale110};
    }
  `,
  medium: css`
    height: ${scale130};
    width: ${scale130};

    & > svg {
      height: ${scale130};
      width: ${scale130};
    }
  `,
  large: css`
    height: ${scale130};
    width: ${scale130};

    & > svg {
      height: ${scale130};
      width: ${scale130};
    }
  `,
};

// *** Base ***
const baseButton = css`
  align-items: center;
  background-color: transparent;
  border-radius: 50%;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  margin: 0;
  padding: 0;
  position: relative;
  text-decoration: none;
  vertical-align: top;
  z-index: ${zIndexContentLayer2};

  &::after {
    background-color: transparent;
    border-radius: 50%;
    box-shadow: ${dropShadowM};
    box-sizing: border-box;
    content: '';
    height: 100%;
    inset-block-start: 0;
    inset-inline-start: 0;
    position: absolute;
    transition-duration: ${motionTimeM};
    transition-property:
      transform, width, height, inset-block-start, inset-inline-start,
      background-color;
    transition-timing-function: ${motionEasingLeave};
    width: 100%;
    z-index: ${zIndexBackgroundLayer1};
  }

  &:hover {
    &::after {
      height: calc(100% + ${scale020} * 2);
      inset-block-start: -${scale020};
      inset-inline-start: -${scale020};
      width: calc(100% + ${scale020} * 2);
    }
  }

  &:active {
    transform: scale(1.125);
    transition-duration: ${motionTimeS};
    transition-property: transform;
    transition-timing-function: ${motionEasingLeave};

    &::after {
      height: calc(100% - ${scale020} * 2);
      inset-block-start: ${scale020};
      inset-inline-start: ${scale020};
      transition-duration: ${motionTimeS};
      width: calc(100% - ${scale020} * 2);
    }
  }

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:disabled {
    cursor: default;
    pointer-events: none;

    &::after {
      background-color: transparent;
      user-select: none;
    }
  }
`;

// *** Icon ***
export const IconContainer = styled.span<IconContainerTProps>`
  ${(props) => css`
    ${props.$size && iconSizes[props.$size]};

    ${props.$sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${iconSizes[props.$sizeConfined]};
      }
    `};

    ${props.$sizeWide &&
    css`
      @media ${mediaWide} {
        ${iconSizes[props.$sizeWide]};
      }
    `};
  `};
`;

export const FloatingButton = styled.button<FloatingButtonTProps>`
  ${baseButton};

  ${(props) => css`
    ${props.$size &&
    css`
      ${sizes[props.$size]};
    `};

    ${props.$sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${sizes[props.$sizeConfined]};
      }
    `};

    ${props.$sizeWide &&
    css`
      @media ${mediaWide} {
        ${sizes[props.$sizeWide]};
      }
    `};

    ${props.$variant &&
    css`
      ${variants[props.$variant]};
    `};
  `};
`;

export const RouterFloatingButton = styled(Link)<FloatingButtonTProps>`
  ${baseButton};

  ${(props) => css`
    ${props.$size &&
    css`
      ${sizes[props.$size]};
    `};

    ${props.$sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${sizes[props.$sizeConfined]};
      }
    `};

    ${props.$sizeWide &&
    css`
      @media ${mediaWide} {
        ${sizes[props.$sizeWide]};
      }
    `};

    ${props.$variant &&
    css`
      ${variants[props.$variant]};
    `};
  `};
`;

const smallLoading = css`
  transform: translate(-50%, -50%) scale(0.75);
`;

const mediumLoading = css`
  transform: translate(-50%, -50%) scale(0.9);
`;

const largeLoading = css`
  transform: translate(-50%, -50%);
`;

export const Loading = styled(DotLoadingIndicator)<LoadingTProps>`
  inset-block-start: 50%;
  inset-inline-start: 50%;
  position: absolute;
  transform: translate(-50%, -50%);

  ${(props) => css`
    ${props.$size === 'small' && smallLoading};
    ${props.$size === 'medium' && mediumLoading};

    ${props.$sizeConfined === 'large' &&
    css`
      ${largeLoading};
    `};
    ${props.$sizeConfined === 'medium' &&
    css`
      @media ${mediaConfined} {
        ${mediumLoading};
      }
    `};
    ${props.$sizeConfined === 'small' &&
    css`
      @media ${mediaConfined} {
        ${smallLoading};
      }
    `};

    ${props.$sizeWide === 'large' &&
    css`
      ${largeLoading};
    `};
    ${props.$sizeWide === 'medium' &&
    css`
      @media ${mediaWide} {
        ${mediumLoading};
      }
    `};
    ${props.$sizeWide === 'small' &&
    css`
      @media ${mediaWide} {
        ${smallLoading};
      }
    `};
  `};
`;
